import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { triggerToast } from '@/components/Toast'
import { Separator, Spinner } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import {
  useTransferComment,
  useTransferCommentMutation,
} from './hooks/useTransferComment'
import { TransferItem } from './TransferItem'

export interface TransferCommentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const TransferCommentDialog: React.FC<TransferCommentDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation()

  const {
    commentsTransfer,
    listVersion,
    sourceVersionId,
    destinationVersionId,
  } = useTransferComment()
  const { mutate: transferCommentMutation, isPending } =
    useTransferCommentMutation()
  const [selectedCommentIds, setSelectedCommentIds] = useState<Set<string>>(
    new Set()
  )
  // Check if all comments are selected
  const selectAll = useMemo(
    () =>
      commentsTransfer.length > 0 &&
      selectedCommentIds.size === commentsTransfer.length,
    [selectedCommentIds.size, commentsTransfer.length]
  )

  const handleCancel = useCallback(() => {
    onOpenChange(false)
    setSelectedCommentIds(new Set())
  }, [onOpenChange])

  const handleSave = useCallback(async () => {
    const transfer_comments = Array.from(selectedCommentIds).map((id) => ({
      id,
    }))

    const payLoad = {
      source_document_version_id: sourceVersionId,
      destination_document_version_id: destinationVersionId,
      transfer_comments,
    }

    transferCommentMutation(payLoad, {
      onSuccess: () => {
        triggerToast(
          'success',
          t('tflViewer.transferCommentDialog.messages.transferSuccess')
        )
        onOpenChange(false)
        setSelectedCommentIds(new Set())
      },
      onError: () => {
        triggerToast(
          'error',
          t('tflViewer.transferCommentDialog.messages.errorGeneric')
        )
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sourceVersionId,
    destinationVersionId,
    selectedCommentIds,
    transferCommentMutation,
    onOpenChange,
  ])

  const handleChangeSelectAll = useCallback(() => {
    if (selectAll) {
      // Deselect all
      setSelectedCommentIds(new Set())
    } else {
      // Select all
      const allIds = new Set(commentsTransfer.map((comment) => comment.id))
      setSelectedCommentIds(allIds)
    }
  }, [selectAll, commentsTransfer])

  const handleToggleComment = useCallback((commentId: string) => {
    setSelectedCommentIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(commentId)) {
        newSet.delete(commentId)
      } else {
        newSet.add(commentId)
      }
      return newSet
    })
  }, [])

  return (
    <>
      {open && (
        <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-[720px] sm:max-w-[720px] p-0 min-h-[720px] ">
            <DialogHeader className="p-6 pb-0">
              <DialogTitle className="mb-1">
                {t('tflViewer.transferCommentDialog.title')}
              </DialogTitle>
              <DialogDescription className="!mt-0 text-tertiary-600">
                {t('tflViewer.transferCommentDialog.description')}
              </DialogDescription>
            </DialogHeader>

            <Separator />

            <div className="h-[500px] p-6 pt-0">
              <div className="flex">
                <div className="flex flex-col flex-1 gap-1">
                  <div className="text-sm font-medium text-secondary-700">
                    {t('tflViewer.transferCommentDialog.from')}
                  </div>
                  <div className="font-semibold text-md text-primary-900">
                    Version {`${listVersion.length - 1}.0`}
                  </div>
                </div>
                <div className="flex flex-col flex-1 gap-1">
                  <div className="text-sm font-medium text-secondary-700">
                    {t('tflViewer.transferCommentDialog.to')}
                  </div>
                  <div className="font-semibold text-md text-primary-900">
                    Version {`${listVersion.length}.0`}
                  </div>
                </div>
              </div>
              <div className="mt-5 border border-solid rounded-md border-primary">
                <div className="flex justify-between px-5 py-3">
                  <div className="text-sm font-semibold text-primary-900">
                    Selected ({selectedCommentIds.size})
                  </div>

                  <Checkbox
                    checked={selectAll}
                    onChange={handleChangeSelectAll}
                    label={t('tflViewer.transferCommentDialog.selectAll')}
                    labelClassName="text-sm text-secondary-700"
                  />
                </div>
                <Separator />
                <div className="p-5 h-[380px] overflow-y-auto flex flex-col gap-3">
                  {commentsTransfer.map((comment) => (
                    <TransferItem
                      key={comment.id}
                      comment={comment}
                      isSelected={selectedCommentIds.has(comment.id)}
                      onToggle={handleToggleComment}
                    />
                  ))}
                </div>
              </div>
            </div>
            <Separator />
            <div className="p-6 pt-0 text-right">
              <Button
                className="rounded-md py-2.5 px-3.5 text-sm font-semibold border-solid border-primary bg-white shadow-sm text-secondary-700 mr-3"
                variant="default"
                onClick={handleCancel}
              >
                {t('tflViewer.addCommentDialog.cancel')}
              </Button>
              <Button
                className="rounded-md py-2.5 px-3.5 text-sm whitespace-nowrap font-semibold border-solid border-transparent text-white bg-brand-solid hover:text-white hover:bg-brand-solid"
                variant="default"
                onClick={handleSave}
                disabled={selectedCommentIds.size === 0 || isPending}
              >
                {isPending ? (
                  <div className="flex items-center gap-2 px-1">
                    <Spinner className="size-4" />
                    {t('tflViewer.transferCommentDialog.saving')}
                  </div>
                ) : (
                  t('tflViewer.transferCommentDialog.save')
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
